import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { CountryInput } from '@/components/inputs/CountryInput'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import {
  areSelectOptionsEqual,
  type SelectOption,
} from '@/components/inputs/select-option'
import { US_STATES_AND_TERRITORIES } from '@/assets/data/us-states-territories'
import { type PhysicalAddressEditorViewModel } from '@/features/jobs/info-gathering/PhysicalAddressEditorViewModel'

const US_STATE_OPTIONS: SelectOption[] = US_STATES_AND_TERRITORIES.map(
  ([code, label]) => ({ id: code, text: label }),
)

const stateItemToString = (item: SelectOption) => item.text

export interface PhysicalAddressEditorContainerProps {
  vm: PhysicalAddressEditorViewModel
  /**
   * Prefix for the test IDs of the fields.
   */
  testId?: string
}

/**
 * Fields for editing a physical address, with a state picker for US
 * addresses.
 */
export const PhysicalAddressEditorContainer = observer(
  function PhysicalAddressEditorContainer({
    vm,
    testId,
  }: PhysicalAddressEditorContainerProps) {
    const stateItemSource = useMemo(
      () =>
        ItemSource.static<SelectOption>(US_STATE_OPTIONS, (item, text) =>
          item.text.toLowerCase().includes(text.toLowerCase()),
        ),
      [],
    )
    const stateSelectProps = useCustomSelectStateFromItemSource({
      itemSource: stateItemSource,
    })
    const selectedState = US_STATE_OPTIONS.find((o) => o.id === vm.region)

    return (
      <>
        <TextInputContainer
          model={vm}
          field="addressLine1"
          name="addressLine1"
          label="Address line 1"
          error={vm.validation.getError('addressLine1')}
          data-test={`${testId}-address-line1`}
        />
        <TextInputContainer
          model={vm}
          field="addressLine2"
          name="addressLine2"
          label="Address line 2"
          sublabel="(optional)"
          error={vm.validation.getError('addressLine2')}
          data-test={`${testId}-address-line2`}
        />
        <TextInputContainer
          model={vm}
          field="city"
          name="city"
          label="City"
          error={vm.validation.getError('city')}
          data-test={`${testId}-city`}
        />
        <TextInputContainer
          model={vm}
          field="postalCode"
          name="postalCode"
          label="ZIP / Postal code"
          error={vm.validation.getError('postalCode')}
          data-test={`${testId}-postal-code`}
        />
        {vm.country === 'US' ? (
          <FormGroupContainer
            label="State or territory"
            htmlFor="region"
            error={vm.validation.getError('region')}
          >
            <CustomSelect<SelectOption, false>
              {...stateSelectProps}
              multi={false}
              value={selectedState}
              itemToString={stateItemToString}
              areItemsEqual={areSelectOptionsEqual}
              placeholder="Select state"
              onChange={(selection) => vm.setRegion(selection?.id ?? '')}
              testId={`${testId}-region`}
            />
          </FormGroupContainer>
        ) : (
          <TextInputContainer
            model={vm}
            field="region"
            name="region"
            label="Region"
            error={vm.validation.getError('region')}
            data-test={`${testId}-region`}
          />
        )}
        <FormGroupContainer
          label="Country"
          htmlFor="country"
          error={vm.validation.getError('country')}
        >
          <CountryInput
            value={vm.country}
            onChange={vm.setCountry}
            testId={`${testId}-country`}
          />
        </FormGroupContainer>
      </>
    )
  },
)
